// @ts-nocheck

import React, { useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';
import {
  FastField,
  Field,
  Formik
} from 'formik';
import type {
  FieldInputProps,
  FormikHelpers,
  FormikErrors,
  FormikProps
} from 'formik';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import { FormItem } from './ant/Form';

export type ValidateStatus =
  | 'success'
  | 'warning'
  | 'error'
  | 'validating'
  | any;

type FormFieldChildrenProps = {
  value: any,
  setValue: (value: any) => void,
  target: ReactNode,
  defaultValue?: any
};

/**
 * Компонент для отрисовки содержимого поля формы
 * и установки значения по умолчанию
 */
const FormFieldChildren = ({
  value,
  setValue,
  target,
  defaultValue
}: FormFieldChildrenProps) => {
  useEffect(() => {
    if (!isNil(defaultValue) && isNil(value)) {
      setValue(defaultValue);
      // На сервер лучше не отсылать пустые строки
    } else if (value === '') setValue(undefined);
  }, [value, defaultValue, setValue]);

  return target;
};

export type FormFieldProps = {
  name?: string,
  required?: boolean,
  error?: string,
  hasFeedback?: boolean,
  label?: string,
  children: (field: FieldInputProps<any>) => ReactNode,
  validate?: (value: any) => string | null,
  validateStatus?: (
    value: any,
    error: string
  ) => ValidateStatus | null,
  // Функция для отображения вспомогательного текста (для ошибок и т.д)
  help?: ((value: any, error: string) => string | null) | string | null,
  // использовать FastField
  fast?: boolean,
  // текст ошибки
  requiredErrorMessage?: string,
  className?: string,
  style?: any,
  // Значение по-умолчанию, если в onChange приходит null
  defaultValue?: any,
  disableHelp?: boolean,
  renderHelp?: (error: string) => ReactNode
};

export type FormFieldType = ComponentType<FormFieldProps>;

export const FormField = ({
  required,
  requiredErrorMessage = 'Обязательное поле',
  error,
  hasFeedback = true,
  name,
  children,
  label,
  validate,
  fast = false,
  className,
  help,
  validateStatus,
  defaultValue = null,
  disableHelp,
  style,
  renderHelp
}: FormFieldProps) => {
  const FormField = fast ? FastField : Field;
  return (
    <FormField
      name={name}
      validate={(value: string) => {
        if (required && isNil(value)) {
          return requiredErrorMessage;
        } else if (typeof validate === 'function') {
          return validate(value);
        }
      }}
      render={({ field, form }) => {
        const { value } = field;
        const { errors, setFieldValue } = form;
        const helpMessage = disableHelp
          ? null
          : typeof help === 'function'
          ? help(value, get(errors, name))
          : get(errors, name) || help;
        return (
          <FormItem
            style={style}
            className={className}
            label={label}
            required={required}
            hasFeedback={hasFeedback}
            validateStatus={
              typeof validateStatus === 'function'
                ? validateStatus(value, get(errors, name))
                : get(errors, name)
                ? 'error'
                : validateStatus
            }
            help={!renderHelp && helpMessage}
          >
            <FormFieldChildren
              target={children(field)}
              value={field.value}
              setValue={value => setFieldValue(field.name, value)}
              defaultValue={defaultValue}
            />
            {renderHelp && helpMessage ? renderHelp(helpMessage) : null}
          </FormItem>
        );
      }}
    />
  );
};

FormField.defaultProps = {
  help: (value, error) => error,
  validateStatus: (value, error) => error && 'error'
};

type FormProps = {
  onSubmit?: (values: any, formikActions: FormikHelpers<any>) => Promise<void>,
  onValidationError?: (...args: any[]) => any,
  validate?: (values: any) => FormikErrors<any>,
  validationSchema?: any,
  initialValues: any,
  children: (
    FormField: ComponentType<FormFieldProps>,
    formikProps: FormikProps<any>
  ) => ReactNode
};

const Form = ({ children, validate, ...formikProps }: FormProps) => (
  <Formik
    {...formikProps}
    onSubmit={async (values, actions) => {
      const { onSubmit } = formikProps;
      onSubmit && (await onSubmit(values, actions));
      actions.setSubmitting(false);
    }}
    initialValues={formikProps.initialValues || {}}
    enableReinitialize
    render={(formikProps: FormikProps<any>) => children(FormField, formikProps)}
    validate={(values: any) => {
      if (validate !== undefined) {
        let errors = validate(values);
        return pickBy(errors, value => !isEmpty(value));
      }
    }}
  />
);

export default Form;
